import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { useAuthStore } from '~/store/auth.store';
import { extractApiError } from '~/utils/extract-api-error';

import {
  getEnrichmentValidationFacets,
  getEnrichmentValidationProducts,
  getEnrichmentValidationSummary,
  getScopedValidationFacets,
  getValidationActionCapabilities,
  repairEnrichmentRun,
  revalidateEnrichmentRun,
  searchValidatedProducts,
  type ValidationConnectorCode,
} from '../services/enrichment-validation.service';
import type {
  EnrichmentValidationProductFilters,
  EnrichmentValidationSummary,
  ValidationScope,
} from '../types/enrichment-validation.api.types';

/** Poll cadence (contract: 3–5 s) for the summary while RUNNING, after a revalidate / repair, and
 *  for the summary + action gates while a re-enrichment or repair is in flight. */
export const ENRICHMENT_VALIDATION_POLL_MS = 4_000;

/** Ceiling on the post-revalidate (or post-repair) watch; a run still RUNNING by then keeps polling on its own. */
export const REVALIDATION_WATCH_BUDGET_MS = 60_000;

/** Products per "View products" page (the server allows 1–100; its default is 50). */
export const ENRICHMENT_VALIDATION_PAGE_SIZE = 50;

/** An accepted revalidate awaiting its run (contract §5), tagged with the run it was made for so a
 *  batch switch on the same mounted page can never inherit it. */
interface RevalidationWatch {
  workflowId: string;
  /** The run the revalidate started (`null` when it joined one already executing). */
  startedRunId: string | null;
  /** A summary read `RUNNING` since the click. */
  sawRunning: boolean;
  startedAt: number;
}

/** True until the revalidate's run has landed: RUNNING was seen and left, or the started run is
 *  the deciding one and no longer RUNNING. `null` summary (route not served) waits for nothing. */
function isAwaitingRevalidation(
  watch: RevalidationWatch | null,
  workflowId: string,
  summary: EnrichmentValidationSummary | null | undefined,
): boolean {
  // `watch?.workflowId` is undefined without a watch, so one check covers "none" and "other run".
  if (watch?.workflowId !== workflowId || summary === null) return false;
  if (summary === undefined || summary.status === 'RUNNING') return true;
  const landed =
    watch.sawRunning ||
    (watch.startedRunId !== null && summary.validationRunId === watch.startedRunId);
  return !landed;
}

/** The page-owned validation summary (banner + drawer), read by the run's root id. Polls while
 *  RUNNING and during a revalidate watch (shown as RUNNING), bounded by the watch budget. */
export function useEnrichmentValidationSummary(
  workflowId: string,
  connectorCode: ValidationConnectorCode,
  enabled: boolean,
) {
  const [watch, setWatch] = useState<RevalidationWatch | null>(null);

  const query = useQuery({
    queryKey: queryKeys.catalogOperations.enrichmentValidationSummary(workflowId),
    queryFn: () => getEnrichmentValidationSummary(workflowId, connectorCode),
    enabled: enabled && Boolean(workflowId),
    staleTime: 0,
    // Re-evaluated by TanStack on every render and after every fetch, so it
    // always sees the current `watch`; the interval dies with the last observer.
    refetchInterval: (q) =>
      q.state.data?.status === 'RUNNING' || isAwaitingRevalidation(watch, workflowId, q.state.data)
        ? ENRICHMENT_VALIDATION_POLL_MS
        : false,
  });

  // Records "RUNNING observed" during render (React's adjust-state-on-change pattern), so a RUNNING
  // read by any observer of this summary — the Re-enrich poll included — counts.
  if (watch?.workflowId === workflowId && !watch.sawRunning && query.data?.status === 'RUNNING') {
    setWatch({ ...watch, sawRunning: true });
  }

  // Bounds the watch: a timer (cleared on unmount / re-arm) drops it at the budget, which in turn
  // switches the poll above off. Keyed on the start time so a `sawRunning` flip doesn't re-arm it.
  const watchStartedAt = watch?.startedAt;
  useEffect(() => {
    if (watchStartedAt === undefined) return;
    const remaining = watchStartedAt + REVALIDATION_WATCH_BUDGET_MS - Date.now();
    const timer = setTimeout(
      () => {
        setWatch(null);
      },
      Math.max(0, remaining),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [watchStartedAt]);

  return {
    /** `undefined` until loaded (or while disabled); `null` when the route is not served. */
    summary: query.data,
    isError: query.isError,
    isAwaitingRevalidation: isAwaitingRevalidation(watch, workflowId, query.data),
    /** Starts the bounded post-revalidate poll; `startedRunId` is the response's `validationRunId`. */
    startRevalidationWatch: (startedRunId: string | null) => {
      setWatch({ workflowId, startedRunId, sawRunning: false, startedAt: Date.now() });
    },
  };
}

/** workflow-service's Re-enrich (`invalidItemsRetry`) and Fix (`repairAction`) gates for the run,
 *  from one run-detail read. While either is in flight it polls itself and the summary, and
 *  re-reads the summary when it ends. Each gate is `null` when not offered. */
export function useValidationActionCapabilities(
  runId: string,
  summaryWorkflowId: string,
  connectorCode: ValidationConnectorCode,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.workflowRuns.validationActions(runId),
    queryFn: () => getValidationActionCapabilities(runId),
    enabled: enabled && Boolean(runId),
    staleTime: 0,
    refetchInterval: (q) =>
      q.state.data?.invalidItemsRetry?.inFlight || q.state.data?.repairAction?.inFlight
        ? ENRICHMENT_VALIDATION_POLL_MS
        : false,
  });
  const inFlight =
    enabled &&
    (query.data?.invalidItemsRetry?.inFlight === true ||
      query.data?.repairAction?.inFlight === true);

  // A second observer on the summary: polls it while the re-enrichment / repair runs (§2).
  useQuery({
    queryKey: queryKeys.catalogOperations.enrichmentValidationSummary(summaryWorkflowId),
    queryFn: () => getEnrichmentValidationSummary(summaryWorkflowId, connectorCode),
    enabled: inFlight && Boolean(summaryWorkflowId),
    refetchInterval: ENRICHMENT_VALIDATION_POLL_MS,
  });

  // The retry's / repair's re-validation is recorded RUNNING before it ends, so re-read the summary
  // the moment `inFlight` drops rather than re-offering the action on a stale COMPLETED.
  const wasInFlight = useRef(false);
  useEffect(() => {
    if (wasInFlight.current && !inFlight) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.enrichmentValidationSummary(summaryWorkflowId),
      });
    }
    wasInFlight.current = inFlight;
  }, [inFlight, queryClient, summaryWorkflowId]);

  return {
    invalidItemsRetry: query.data?.invalidItemsRetry ?? null,
    repairAction: query.data?.repairAction ?? null,
  };
}

/** One "View products" page, keyed by the summary's run id + status (so a new or settled run
 *  refetches it), the filters and page; `keepPreviousData` keeps the pager while it loads. */
export function useEnrichmentValidationProducts(
  workflowId: string,
  connectorCode: ValidationConnectorCode,
  run: Pick<EnrichmentValidationSummary, 'validationRunId' | 'status'> | null | undefined,
  filters: EnrichmentValidationProductFilters & { page: number },
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.enrichmentValidationProducts(
      workflowId,
      run?.validationRunId ?? '',
      run?.status ?? '',
      {
        status: filters.status,
        q: filters.q ?? '',
        rule: filters.rule ?? '',
        field: filters.field ?? '',
        reenrich: filters.reenrich ?? '',
        sort: filters.sort,
        page: filters.page,
      },
    ),
    queryFn: () =>
      getEnrichmentValidationProducts(workflowId, connectorCode, {
        ...filters,
        size: ENRICHMENT_VALIDATION_PAGE_SIZE,
      }),
    enabled: enabled && Boolean(workflowId),
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/** The drawer's filter-menu counts, keyed like the pages on the run state, then `status` + `q`
 *  only (fetched once per change, not per page); the previous counts stay up while it loads. */
export function useEnrichmentValidationFacets(
  workflowId: string,
  connectorCode: ValidationConnectorCode,
  run: Pick<EnrichmentValidationSummary, 'validationRunId' | 'status'> | null | undefined,
  filters: Pick<EnrichmentValidationProductFilters, 'status' | 'q'>,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.enrichmentValidationFacets(
      workflowId,
      run?.validationRunId ?? '',
      run?.status ?? '',
      filters.status,
      filters.q ?? '',
    ),
    queryFn: () => getEnrichmentValidationFacets(workflowId, connectorCode, filters),
    enabled: enabled && Boolean(workflowId),
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/** How long a cross-run read stays fresh (see `useScopedValidatedProducts`). */
const SCOPED_STALE_MS = 30 * 1000;

/** One page of a cross-run products list. `digest` identifies the scopes (the caller already
 *  hashes them), so the key stays small however many ids they hold. */
export function useScopedValidatedProducts(
  scopes: readonly ValidationScope[],
  connectorCode: ValidationConnectorCode,
  digest: string,
  filters: EnrichmentValidationProductFilters & { page: number },
  enabled: boolean,
) {
  return useQuery({
    queryKey: [
      ...queryKeys.catalogOperations.enrichmentValidationSearch(digest),
      'products',
      filters.status,
      filters.q ?? '',
      filters.rule ?? '',
      filters.field ?? '',
      filters.sort,
      filters.page,
    ],
    queryFn: () =>
      searchValidatedProducts(scopes, connectorCode, {
        ...filters,
        size: ENRICHMENT_VALIDATION_PAGE_SIZE,
      }),
    enabled: enabled && scopes.length > 0,
    // Each read re-sends every scope's ids (up to ~1 MB): reopening the list within 30 s
    // reads the cache; any run's re-run or fix invalidates every cross-run list.
    staleTime: SCOPED_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

/** A cross-run list's filter-menu and tab counts under `status` + `q`. */
export function useScopedValidationFacets(
  scopes: readonly ValidationScope[],
  connectorCode: ValidationConnectorCode,
  digest: string,
  filters: Pick<EnrichmentValidationProductFilters, 'status' | 'q'>,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [
      ...queryKeys.catalogOperations.enrichmentValidationSearch(digest),
      'facets',
      filters.status,
      filters.q ?? '',
    ],
    queryFn: () => getScopedValidationFacets(scopes, connectorCode, filters),
    enabled: enabled && scopes.length > 0,
    // Each read re-sends every scope's ids (up to ~1 MB): reopening the list within 30 s
    // reads the cache; any run's re-run or fix invalidates every cross-run list.
    staleTime: SCOPED_STALE_MS,
    placeholderData: keepPreviousData,
  });
}

/** HTTP status of a failed call, when it was an API error. */
function statusOf(error: unknown): number | null {
  return error instanceof ApiError ? error.statusCode : null;
}

/** Toasts a failed validation action: our status-specific headline, with the
 *  server's own reason (via `extractApiError`) as the detail line. */
function toastActionError(error: unknown, headline: string, tone: 'warning' | 'error'): void {
  const detail = extractApiError(error, '');
  const options = detail && detail !== headline ? { description: detail } : undefined;
  if (tone === 'warning') {
    toast.warning(headline, options);
  } else {
    toast.error(headline, options);
  }
}

/** Starts a FULL validation ("Run / Re-run validation", a recompute) and hands the started run's
 *  id to `onStarted`, which shows RUNNING and polls until it lands (§5). */
export function useRevalidateEnrichmentRun(
  workflowId: string,
  connectorCode: ValidationConnectorCode,
  onStarted: (startedRunId: string | null) => void,
) {
  const { t } = useTranslation('catalog-operations');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => revalidateEnrichmentRun(workflowId, connectorCode),
    onSuccess: (run) => {
      toast.success(t('enrichmentValidation.revalidate.started'));
      if (run.started) onStarted(run.validationRunId);
    },
    onError: (error) => {
      // 409 = already running (the settle re-read shows RUNNING and polls) or a scores-only run;
      // the server's message, as the detail line, says which.
      const status = statusOf(error);
      if (status === 409) {
        toastActionError(error, t('enrichmentValidation.revalidate.errors.conflict'), 'warning');
      } else if (status === 404) {
        toastActionError(error, t('enrichmentValidation.revalidate.errors.notFound'), 'warning');
      } else if (status === 503) {
        toastActionError(error, t('enrichmentValidation.revalidate.errors.unavailable'), 'error');
      } else {
        toastActionError(error, t('enrichmentValidation.revalidate.errors.failed'), 'error');
      }
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.catalogOperations.enrichmentValidationAll(workflowId),
        }),
        // Any cross-run list may hold this run's products.
        queryClient.invalidateQueries({
          queryKey: queryKeys.catalogOperations.enrichmentValidationSearchAll(),
        }),
      ]),
  });
}

/** "Fix automatically" (workflow-service repair) for the run's root `runId`. A dispatched repair
 *  starts the summary watch (`onStarted`) and the gate's `inFlight` polls until it lands. */
export function useRepairEnrichmentRun(
  workflowId: string,
  runId: string,
  onStarted: (startedRunId: string | null) => void,
) {
  const { t } = useTranslation('catalog-operations');
  const queryClient = useQueryClient();
  const email = useAuthStore((s) => s.user?.email);
  return useMutation({
    // `productIds` scopes it to those products (a single product's Fix); omitted = the whole run.
    // `void` keeps `mutate()` argument-free for the whole-run callers (TanStack v5 only makes
    // the variables optional when they include `void`).
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    mutationFn: (productIds: readonly string[] | void) =>
      repairEnrichmentRun(runId, email, productIds),
    onSuccess: (response) => {
      if (!response.applied) {
        // Nothing dispatched: the server's sentence says why (rendered as text).
        toast.info(
          t('enrichmentValidation.repair.nothingToDo'),
          response.detail ? { description: response.detail } : undefined,
        );
        return;
      }
      const count = response.retriedCount ?? 0;
      toast.success(
        t('enrichmentValidation.repair.started', { count, formatted: count.toLocaleString() }),
      );
      // The repair's own re-validation has no run id yet: watch for RUNNING to come and go.
      onStarted(null);
    },
    onError: (error) => {
      // 409: validation not COMPLETED / a repair or retry in flight / unsupported — the server
      // message, as the detail line, says which.
      const status = statusOf(error);
      if (status === 409) {
        toastActionError(error, t('enrichmentValidation.repair.errors.conflict'), 'warning');
      } else if (status === 503) {
        toastActionError(error, t('enrichmentValidation.repair.errors.busy'), 'error');
      } else if (status === 502) {
        toastActionError(error, t('enrichmentValidation.repair.errors.unavailable'), 'error');
      } else if (status === 404) {
        toastActionError(error, t('enrichmentValidation.repair.errors.notTracked'), 'error');
      } else {
        toastActionError(error, t('enrichmentValidation.repair.errors.failed'), 'error');
      }
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.workflowRuns.validationActions(runId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.catalogOperations.enrichmentValidationAll(workflowId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.catalogOperations.enrichmentValidationSearchAll(),
        }),
      ]),
  });
}
